import { useEffect, useState } from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  formatAbsoluteDateTime,
  formatRelativeTime,
  formatRelativeTimeInDays,
} from '@/utils/relative-time'
import { cn } from '@/lib/utils'

interface RelativeTimeProps {
  value: string | number | Date | null | undefined
  /** Optional prefix word, e.g. "Updated" → "Updated 3 days ago". */
  prefix?: string
  /** `days` uses calendar-day bands only (today, yesterday, N days ago). */
  unit?: 'default' | 'days'
  className?: string
  /** Re-render interval in ms (defaults to 60s; pass 0 to disable). */
  tickMs?: number
}

export function RelativeTime({
  value,
  prefix,
  unit = 'default',
  className,
  tickMs = 60_000,
}: RelativeTimeProps) {
  const [, force] = useState(0)

  useEffect(() => {
    if (!tickMs) return
    const id = window.setInterval(() => force((n) => n + 1), tickMs)
    return () => window.clearInterval(id)
  }, [tickMs])

  if (value == null) return null
  const relative =
    unit === 'days' ? formatRelativeTimeInDays(value) : formatRelativeTime(value)
  if (!relative) return null
  const absolute = formatAbsoluteDateTime(value)

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <time className={cn('whitespace-nowrap', className)} dateTime={new Date(value).toISOString()}>
          {prefix ? `${prefix} ` : ''}
          {relative}
        </time>
      </TooltipTrigger>
      <TooltipContent>{absolute}</TooltipContent>
    </Tooltip>
  )
}
